import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'] as const;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const BugReportFormSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1, 'Please describe the issue before sending.')
    .max(5000, 'Message must be 5000 characters or fewer'),
  images: z
    .array(z.instanceof(File))
    .max(2, 'You can attach up to 2 images')
    .refine(
      (files) =>
        files.every((file) =>
          (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)
        ),
      'Only PNG and JPEG images are allowed'
    )
    .refine(
      (files) => files.every((file) => file.size <= MAX_IMAGE_BYTES),
      'Each image must be 10 MB or smaller'
    ),
});

export type BugReportFormValues = z.infer<typeof BugReportFormSchema>;

// Status vocabulary (mirrors Zendesk states surfaced by the backend, spec §6 / §2.1)
export const SupportStatusSchema = z.enum([
  'new',
  'open',
  'pending',
  'solved',
  'closed',
]);
export type SupportStatus = z.infer<typeof SupportStatusSchema>;

// Message direction — who authored a comment (spec §2.2)
export const SupportMessageFromSchema = z.enum(['client', 'agent', 'system']);
export type SupportMessageFrom = z.infer<typeof SupportMessageFromSchema>;

// List item — spec §2.1
export const SupportRequestSchema = z.object({
  id: z.string(),
  subject: z.string(),
  status: SupportStatusSchema,
  location: z.string().nullable(),
  location_timezone: z.string().nullable(),
  submitted_at: zodTimeStamp,
  updated_at: zodTimeStamp,
  unread_count: z.number().int().min(0),
  // Direction of the last public message, so the list row can derive the same
  // "whose turn?" display status as the conversation header (deriveDisplayStatus).
  // Nullable when the ticket has no public messages; optional until the backend
  // ships the field (the row falls back to the raw status meanwhile).
  last_message_from: SupportMessageFromSchema.nullable().optional(),
});
export type SupportRequest = z.infer<typeof SupportRequestSchema>;

// Attachment — spec §2.2
export const SupportAttachmentSchema = z.object({
  id: z.string(),
  file_name: z.string(),
  content_type: z.string(),
  // The create-reply (201) response returns an empty url until Zendesk resolves
  // the attachment; the resolved url arrives on the subsequent GET refetch.
  url: z.string().url().or(z.literal('')),
  size_bytes: z.number().int().nonnegative(),
});
export type SupportAttachment = z.infer<typeof SupportAttachmentSchema>;

// Message — spec §2.2
export const SupportMessageSchema = z.object({
  id: z.string(),
  from: SupportMessageFromSchema,
  author: z.string().nullable(),
  text: z.string(),
  created_at: zodTimeStamp,
  attachments: z.array(SupportAttachmentSchema).default([]),
});
export type SupportMessage = z.infer<typeof SupportMessageSchema>;

// Detail (thread) — spec §2.2
export const SupportRequestDetailSchema = SupportRequestSchema.extend({
  follow_up_to: z.string().nullable(),
  messages: z.array(SupportMessageSchema),
});
export type SupportRequestDetail = z.infer<typeof SupportRequestDetailSchema>;

// Create-response (minimum) — spec §2.3 / §2.5
export const CreatedSupportRequestSchema = z.object({
  id: z.string(),
  status: SupportStatusSchema,
  follow_up_to: z.string().nullable().optional(),
});
export type CreatedSupportRequest = z.infer<typeof CreatedSupportRequestSchema>;

// Support category — a required, shared taxonomy used to label every new
// request for analytics (spec §2.3). The VALUE (key) is the cross-repo
// contract: parking-app-api and peo-native MUST use these exact keys. The label
// is portal-only UI. Ordered by expected frequency; `other` always last.
const SUPPORT_CATEGORY_VALUES = [
  'general',
  'technical_issue',
  'enforcement',
  'payments_refunds',
  'other',
] as const;

export const SupportCategorySchema = z.enum(SUPPORT_CATEGORY_VALUES);
export type SupportCategory = z.infer<typeof SupportCategorySchema>;

// Form-field validator: the Select starts unselected (value undefined/''), so we
// normalise to '' and refine with an explicit message. An explicit refinement
// message bypasses the resolver's error-map wrapping, giving a clean required
// hint rather than a humanised enum dump.
const SupportCategoryFieldSchema = z.preprocess(
  (value) => value ?? '',
  z
    .string()
    .refine(
      (value): value is SupportCategory =>
        SUPPORT_CATEGORY_VALUES.some((candidate) => candidate === value),
      { message: 'Please select a category.' }
    )
);

export const SUPPORT_CATEGORY_LABELS: Record<SupportCategory, string> = {
  general: 'General',
  technical_issue: 'Technical Issue',
  enforcement: 'Enforcement',
  payments_refunds: 'Payments & Refunds',
  other: 'Other',
};

// Select options, derived so ordering (the values tuple) and labels stay in one
// place and TS enforces a label for every category.
export const SUPPORT_CATEGORIES: ReadonlyArray<{
  value: SupportCategory;
  label: string;
}> = SUPPORT_CATEGORY_VALUES.map((value) => ({
  value,
  label: SUPPORT_CATEGORY_LABELS[value],
}));

// New request form (RHF) — extends today's BugReportFormSchema with subject +
// required category + optional location (spec §6 / §2.3).
export const NewRequestFormSchema = BugReportFormSchema.extend({
  subject: z
    .string()
    .trim()
    .min(1, 'Subject is required.')
    .max(150, 'Subject must be 150 characters or fewer'),
  category: SupportCategoryFieldSchema,
  location: z.string().optional(),
});
export type NewRequestFormValues = z.infer<typeof NewRequestFormSchema>;

// Reply form (RHF) — text is always required, including when an attachment is
// present: Zendesk rejects a comment that has uploads but an empty body (spec
// §6 / §2.4). See specs/portal-native-support/e2e-findings-2026-06-05.md.
export const ReplyFormSchema = z.object({
  text: z
    .string()
    .trim()
    .min(1, 'Add a message to send.')
    .max(5000, 'Reply must be 5000 characters or fewer'),
  images: BugReportFormSchema.shape.images,
});
export type ReplyFormValues = z.infer<typeof ReplyFormSchema>;

// Client-only optimistic-message shape for the pending bubble — reconciled away
// on the matching server comment (decision #11). Not part of the API contract.
export type OptimisticSupportMessage = SupportMessage & {
  tempId: string;
  isPending: boolean;
};
